import React from 'react';
import TimeAgo from 'react-timeago'

import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faHeart, faTrash} from '@fortawesome/free-solid-svg-icons';

import {Modal, ModalHeader, ModalBody} from 'Components/Modal';
import {Loading} from 'Components/Partials';
import {FormHandler, Textarea, Input} from 'Components/Form';
import {PrimaryButton} from 'Components/Button';

import {Toast} from 'Services';
import User from 'Services/User';
import CommentApi from 'Services/Api/Cms/Elements/Comment';
import {translation} from 'Services/TranslationHelpers';
import { getFallbackAvatarUrl } from "Services/AuthHelpers";

class ViewCmsElementRecordModal extends React.Component {
    /**
     * @var state
     * @type {{item: *, comments: null, loading: boolean}}
     */
    state = {
        item: this.props.item,
        comments: null,
        loading: false
    };

    /**
     * @method componentDidMount
     */
    componentDidMount = () => {
        this.fetchComments();

        CommentApi.on('comment', this.fetchComments);
    };

    /**
     * @method componentWillUnmount
     */
    componentWillUnmount = () => {
        CommentApi.removeListener('comment', this.fetchComments);
    };

    /**
     * @method fetchComments
     * @return {Promise<void>}
     */
    fetchComments = async () => {
        this.setState({
            working: true
        });

        const request = await CommentApi.get(null, {
            cms_element_record_id: this.state.item.id
        });

        if (request.success) {
            this.props.handleCommentNumberUpdate(this.state.item, request.data.data.length);

            return this.setState({
                comments: request.data,
                loading: false
            });
        }

        Toast.error();
    };

    /**
     * @method createComment
     * @param {object} form
     * @return {Promise<*>}
     */
    createComment = (form) => {
        return CommentApi.post(null, {
            ...form,
            cms_element_record_id: this.state.item.id
        }, 'comment');
    };

    /**
     * @method deleteComment
     * @param {string} comment_id
     * @return {Promise<*>}
     */
    deleteComment = async (comment_id) => {
        await CommentApi.delete(comment_id, 'comment');
    };

    /**
     * @method handleToggleLike
     * @return {Promise<void>}
     */
    handleToggleLike = async () => {
        this.setState({
            item: await this.props.handleToggleLike(this.state.item)
        });
    };

    /**
     * @method render
     * @return {JSX.Element}
     */
    render() {
        const {item, loading, comments} = this.state;
        const {title, body, content} = this.props;

        return (
            <Modal>
                <ModalHeader onClose={this.props.onClose} titleClassName="text-app-blue" className="text-app-blue">
                    {title}
                </ModalHeader>

                <ModalBody>
                    {body(item, 'modal')}

                    <div className={"flex justify-between p-2"}>
                        <div>
                            <p className={"light"}>
                                {content}
                            </p>
                        </div>
                        <div>
                            {item.allow_likes && User.loggedIn && (
                                <FontAwesomeIcon
                                    icon={faHeart}
                                    size="1x"
                                    className={`mx-1 cursor-pointer ${item.user_has_liked ? 'text-app-purple' : 'text-app-light-blue'}`}
                                    onClick={() => this.handleToggleLike(item)}
                                />
                            )}
                        </div>
                    </div>

                    {loading && (
                        <Loading />
                    )}

                    {!loading && item.allow_comments && (
                        <React.Fragment>
                            <div className="mx-2 px-2 max-h-40 overflow-y-auto">
                                {comments && comments.data.map(this.renderComment).reverse()}
                            </div>

                            {this.renderCommentForm()}
                        </React.Fragment>
                    )}
                </ModalBody>
            </Modal>
        );
    }

    /**
     * @method renderComment
     * @param {object} comment
     * @return {JSX.Element}
     */
    renderComment = (comment) => {
        return (
            <div key={comment.id} className="flex items-start my-4">
                {(comment.user.avatar_path && comment.user.avatar_path.url ) ? (
                    <img
                        src={comment.user.avatar_path.url}
                        alt={comment.user.full_name}
                        className="w-10 h-10 rounded-full mr-3 mt-2"
                    />
                ) : (
                    <img
                        src={getFallbackAvatarUrl(comment.user)}
                        alt={comment.user.full_name}
                        className="w-10 h-10 rounded-full mr-3 mt-2"
                    />
                )}
            <div className="flex-1 bg-app-gray p-3 rounded-xl text-app-blue">
                <div className="flex justify-between items-center">
                    <p className="font-semibold text-sm">{comment.user.full_name}</p>
                    {comment.user.id === User.data.id && (
                        <FontAwesomeIcon
                            onClick={() => this.deleteComment(comment.id)}
                            icon={faTrash}
                            size="1x"
                            title="Delete comment"
                            className="ml-2 cursor-pointer text-red-200 hover:text-red-500 transition duration-200"
                        />
                    )}
                </div>
                <p className="text-sm mt-1">{comment.message}</p>
                <p className="text-xs text-gray-500 mt-1">
                    <TimeAgo date={comment.created_at.iso_string} />
                </p>
            </div>
        </div>
        );
    };

    /**
     * @method renderCommentForm
     * @return {JSX.Element}
     */
    renderCommentForm = () => {
        const {handleInput, handleSubmit, form} = this.props;

        //if not logged in, don't show the form'
        if (!User.loggedIn) {
            return null;
        }

        return (
            <form onSubmit={(e) => handleSubmit(e, this.createComment, undefined, true)}>
                <div className="mt-4 text-center">
                    <Input
                        type="text"
                        id={"message"}
                        onChange={(v) => handleInput('message', v)}
                        value={form.message ?? ''}
                        placeholder={translation('cms', 'write_comment_here')}
                        required
                    />

                    <div className="w-full mt-2">
                        <PrimaryButton
                            disabled={!form.message || form.message.length === 0}
                            text="Submit"
                            className="transition duration-200 transform hover:scale-110 mt-2"
                        />
                    </div>
                </div>
            </form>
        );
    };
}

export default FormHandler(ViewCmsElementRecordModal);
